import React, { Component } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import moment from "moment";
import { connect } from "react-redux";
import { Result, Select, Space } from "antd";
import { ButtonItem } from "../../../../Components/Items";
import ImportadorExcelGenerico from "../../../../Components/ImportadorExcelGenerico";

const { Option } = Select;

// Misma lista fija que usan los modales manuales de lotes
// (AgregarLotes.js / EditarLote.js) para el campo "Corte" de cada pieza.
const CORTES_VALIDOS = [
  "Entero", "Copina", "Medio Cuerpo", "Backskin", "Panel",
  "Huesos", "Cola", "Patas", "Colmillos", "Garras",
];

const normalizar = (valor) =>
  String(valor ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

const parsearFecha = (valor) => {
  if (!valor && valor !== 0) return undefined;
  const str = String(valor).trim();
  const formatos = ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY", "DD-MM-YYYY", "YYYY/MM/DD", "D/M/YYYY", "DD/MM/YY"];
  for (const fmt of formatos) {
    const m = moment(str, fmt, true);
    if (m.isValid()) return m.toDate();
  }
  return undefined;
};

const parseSiNo = (valor, campo, errores) => {
  const texto = normalizar(valor);
  if (!texto) return false;
  if (texto === "si" || texto === "sí") return true;
  if (texto === "no") return false;
  errores.push({ campo, motivo: 'debe ser "Si" o "No"' });
  return false;
};

const nombreCompletoCliente = (cliente) =>
  `${cliente.nombre || ""} ${cliente.apellidoPaterno || ""} ${cliente.apellidoMaterno || ""}`.trim();

// Fuentes posibles del nombre de un cliente: nombre+apellidos (persona fisica),
// razon social (persona moral) y busquedaNombre (ya precalculado en el backend,
// pero puede venir vacio en clientes antiguos, de ahi el respaldo con las otras dos).
const nombresComparablesCliente = (cliente) =>
  [nombreCompletoCliente(cliente), cliente.razonSocial, cliente.busquedaNombre]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

const nombreMostrarCliente = (cliente) =>
  (cliente.persona && cliente.razonSocial) ? cliente.razonSocial : nombreCompletoCliente(cliente);

const UMBRAL_SIMILITUD_CLIENTE = 0.75;

const distanciaLevenshtein = (a, b) => {
  const filas = a.length + 1;
  const columnas = b.length + 1;
  const matriz = Array.from({ length: filas }, () => new Array(columnas).fill(0));
  for (let i = 0; i < filas; i++) matriz[i][0] = i;
  for (let j = 0; j < columnas; j++) matriz[0][j] = j;
  for (let i = 1; i < filas; i++) {
    for (let j = 1; j < columnas; j++) {
      const costo = a[i - 1] === b[j - 1] ? 0 : 1;
      matriz[i][j] = Math.min(
        matriz[i - 1][j] + 1,
        matriz[i][j - 1] + 1,
        matriz[i - 1][j - 1] + costo
      );
    }
  }
  return matriz[filas - 1][columnas - 1];
};

// Contencion (substring en cualquier direccion) cuenta como coincidencia total;
// si no, se mide tolerancia a errores de dedo con Levenshtein.
const similitudNombres = (a, b) => {
  if (!a || !b) return 0;
  if (a === b || a.includes(b) || b.includes(a)) return 1;
  const maxLen = Math.max(a.length, b.length);
  return 1 - distanciaLevenshtein(a, b) / maxLen;
};

const columnasPreview = [
  {
    title: "Fecha", dataIndex: "fechaOrden", key: "fechaOrden", width: 100,
    render: (v) => (v ? moment(v).format("DD/MM/YYYY") : "-"),
  },
  { title: "Cliente", dataIndex: "_clienteNombre", key: "_clienteNombre", width: 200 },
  { title: "Especie", dataIndex: "_especieNombre", key: "_especieNombre", width: 160 },
  { title: "Numero de Pedido", dataIndex: "numero", key: "numero", width: 130 },
  { title: "Corte", dataIndex: "corte", key: "corte", width: 120 },
  {
    title: "Cuernos", dataIndex: "cuernos", key: "cuernos", width: 80,
    render: (v) => (v ? "Si" : "No"),
  },
  {
    title: "Craneo", dataIndex: "craneo", key: "craneo", width: 80,
    render: (v) => (v ? "Si" : "No"),
  },
];

const construirPlantilla = (catalogos) => {
  const wb = XLSX.utils.book_new();

  const ejemplo = [{
    Fecha: "01/01/2026",
    "Numero de Pedido": "1001",
    Nombre: "Juan",
    "Apellido 1": "Perez",
    "Apellido 2": "Gomez",
    Especie: (catalogos.insumos || [])[0]?.descripcion || "Venado",
    Corte: "Entero",
    Cuernos: "Si",
    Craneo: "No",
  }];
  const hojaPrincipal = XLSX.utils.json_to_sheet(ejemplo);
  XLSX.utils.book_append_sheet(wb, hojaPrincipal, "Comodatos");

  const cortes = CORTES_VALIDOS.map((c) => ({ "Cortes validos": c }));
  const especies = (catalogos.insumos || []).map((i) => ({ "Especies disponibles": i.descripcion }));
  const filasAyuda = cortes.length >= especies.length
    ? cortes.map((c, i) => ({ ...c, ...(especies[i] || {}) }))
    : especies.map((e, i) => ({ ...(cortes[i] || {}), ...e }));
  const hojaAyuda = XLSX.utils.json_to_sheet(filasAyuda.length > 0 ? filasAyuda : [{ "Cortes validos": "-" }]);
  XLSX.utils.book_append_sheet(wb, hojaAyuda, "Ayuda");

  return wb;
};

const validarFila = (fila, catalogos, _indice, _contexto) => {
  const errores = [];

  const fechaOrden = parsearFecha(fila["Fecha"]);
  if (!fechaOrden) errores.push({ campo: "Fecha", motivo: "esta vacia o no tiene un formato de fecha valido" });

  const numero = String(fila["Numero de Pedido"] ?? fila["Número de Pedido"] ?? "").trim();
  if (!numero) errores.push({ campo: "Numero de Pedido", motivo: "esta vacio" });

  const nombre = String(fila["Nombre"] || "").trim();
  const apellido1 = String(fila["Apellido 1"] || "").trim();
  const apellido2 = String(fila["Apellido 2"] || "").trim();
  if (!nombre) errores.push({ campo: "Nombre", motivo: "esta vacio" });

  const nombreBuscado = normalizar(`${nombre} ${apellido1} ${apellido2}`);
  const candidatosCliente = (catalogos.clientes || [])
    .map((c) => ({
      cliente: c,
      score: Math.max(
        0,
        ...nombresComparablesCliente(c).map((n) => similitudNombres(nombreBuscado, normalizar(n)))
      ),
    }))
    .filter((c) => c.score >= UMBRAL_SIMILITUD_CLIENTE)
    .sort((a, b) => b.score - a.score);

  if (nombre && candidatosCliente.length === 0) {
    errores.push({ campo: "Nombre/Apellidos", motivo: "no se encontro un cliente con ese nombre" });
  }
  // candidatosCliente ya viene ordenado por score descendente (sort estable, por lo
  // que en un empate se respeta el orden original del catalogo) — se toma el mejor.
  const clienteEncontrado = candidatosCliente.length > 0 ? candidatosCliente[0].cliente : undefined;

  const especie = String(fila["Especie"] || "").trim();
  if (!especie) errores.push({ campo: "Especie", motivo: "esta vacia" });
  const insumoEncontrado = (catalogos.insumos || []).find(
    (i) => normalizar(i.descripcion) === normalizar(especie)
  );
  if (especie && !insumoEncontrado) {
    errores.push({ campo: "Especie", motivo: "no coincide con ningun insumo" });
  }

  const corte = String(fila["Corte"] || "").trim();
  const corteValido = CORTES_VALIDOS.find((c) => normalizar(c) === normalizar(corte));
  if (!corteValido) {
    errores.push({ campo: "Corte", motivo: `debe ser uno de: ${CORTES_VALIDOS.join(", ")}` });
  }

  const cuernos = parseSiNo(fila["Cuernos"], "Cuernos", errores);
  const craneo = parseSiNo(fila["Craneo"] ?? fila["Cráneo"], "Craneo", errores);

  if (errores.length > 0) {
    return { ok: false, errores };
  }

  return {
    ok: true,
    datos: {
      fechaOrden,
      clienteId: clienteEncontrado._id,
      insumoId: insumoEncontrado._id,
      numero,
      corte: corteValido,
      cuernos,
      craneo,
      _clienteNombre: nombreMostrarCliente(clienteEncontrado),
      _especieNombre: insumoEncontrado.descripcion,
    },
  };
};

class ImportarComodato extends Component {
  state = {
    loadingProyectos: false,
    todosProyectos: [],
    proyectoId: null,
    proyectoNombre: null,
  };

  componentDidMount() {
    this.consultarProyectos();
  }

  consultarProyectos = () => {
    this.setState({ loadingProyectos: true });
    axios
      .post(
        "proyectos/selectProyectos",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ todosProyectos: Array.isArray(res.data) ? res.data : [], loadingProyectos: false });
      })
      .catch((err) => {
        console.log(err);
        this.setState({ loadingProyectos: false });
      });
  };

  elegirProyecto = (proyectoId) => {
    const proyecto = this.state.todosProyectos.find((p) => p._id === proyectoId);
    this.setState({ proyectoId, proyectoNombre: proyecto?.nombre });
  };

  render() {
    const { proyectoId, proyectoNombre, todosProyectos, loadingProyectos } = this.state;

    if (!proyectoId) {
      return (
        <Result
          status="info"
          title="Selecciona primero un Proyecto"
          subTitle="Los comodatos importados desde el Excel se daran de alta en el proyecto que elijas aqui."
          extra={
            <Space>
              <Select
                style={{ width: 260, textAlign: "left" }}
                placeholder="Proyecto"
                loading={loadingProyectos}
                onChange={this.elegirProyecto}
              >
                {todosProyectos.map((p) => (
                  <Option value={p._id} key={p._id}>{p.nombre}</Option>
                ))}
              </Select>
              <ButtonItem onClick={() => this.props.history.push("/panel/comodato")}>
                Cancelar
              </ButtonItem>
            </Space>
          }
        />
      );
    }

    return (
      <ImportadorExcelGenerico
        {...this.props}
        titulo={`Importar Comodatos — Proyecto: ${proyectoNombre || ""}`}
        nombreEntidadPlural="ordenes de comodato"
        endpointDatosImportar="comodato/datosParaImportarComodato"
        endpointImportar="comodato/importarComodato"
        nombrePlantilla="plantillaComodatos.xlsx"
        construirPlantilla={construirPlantilla}
        validarFila={validarFila}
        columnasPreview={columnasPreview}
        prepararPayloadExtra={() => ({ proyectoId })}
        instrucciones={[
          "Fecha: fecha del comodato.",
          "Numero de Pedido: numero de lote del insumo.",
          "Nombre, Apellido 1, Apellido 2: se buscan por coincidencia (no exacta) contra un Cliente ya existente, comparando contra su nombre completo o su razon social; toleran pequeñas diferencias o nombre incompleto. Si varios clientes coinciden, se usa el que mejor coincida.",
          "Especie: nombre del insumo a buscar, tal como aparece en el catalogo.",
          `Corte: una de estas opciones: ${CORTES_VALIDOS.join(", ")}.`,
          "Cuernos / Craneo: Si o No.",
          "Las filas con el mismo Nombre/Apellidos y la misma Fecha se agrupan en una sola Orden de Comodato con varios insumos.",
        ]}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ImportarComodato);
